import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { HeaderActivityService } from './header-activity.service';
import { ToasterService } from '../../../../services/core/toaster.service';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import { ConfirmationPopupComponent } from '../../confirmation-popup/confirmation-popup.component';
import {
  ActivityItem,
  ActivityGroup,
  ActivityFilterType,
  FILTER_CHIPS,
  getActivityIconColor,
  getActivityBgColor,
  getActivityBorderColor,
  getActivityTypeLabel,
} from './activity-panel.types';

const FILTER_MODULE_IDS: Partial<Record<ActivityFilterType, number>> = {
  bids: 16,
  takeoffs: 32,
  proposals: 3,
  jobs: 19,
  customers: 13,
  vendors: 12,
  staff: 11,
};

/**
 * Activity History Panel component.
 * Displays recent user activity with search, filtering, pinning, and date grouping.
 * Triggered from the admin header bar.
 */
@Component({
    selector: 'app-activity-panel',
    templateUrl: './activity-panel.component.html',
    styleUrls: ['./activity-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('200ms ease-out', style({ opacity: 1 })),
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0 })),
            ]),
        ]),
        trigger('slideDown', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-10px)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' })),
            ]),
        ]),
        trigger('slideIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateX(-8px)' }),
                animate('250ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
            ]),
        ]),
    ],
    standalone: false
})
export class ActivityPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() onClose = new EventEmitter<void>();

  activities: ActivityItem[] = [];
  groupedActivities: ActivityGroup[] = [];
  totalCount = 0;
  pinnedCount = 0;
  hasMore = false;
  isLoadingMore = false;
  // True only for the initial/search fetch (loadApiActivities), not pagination
  // (that already has isLoadingMore). Without this, groupedActivities stays []
  // while the request is in flight, and the template showed "No activity yet"
  // for that whole window instead of a loading state.
  isLoading = false;

  currentPage = 1;
  searchQuery = '';
  activeFilter: ActivityFilterType = 'all';

  readonly filterChips = FILTER_CHIPS;

  // Expose utility functions to template
  readonly getIconColor = getActivityIconColor;
  readonly getBgColor = getActivityBgColor;
  readonly getBorderColor = getActivityBorderColor;
  readonly getTypeLabel = getActivityTypeLabel;

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(
    private activityService: HeaderActivityService,
    private cdr: ChangeDetectorRef,
    private toasterService: ToasterService,
    private dialog: NgDialogAnimationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.searchSubject
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe((query) => this.loadApiActivities(query));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
      this.loadApiActivities();
    }
  }

  loadMore(): void {
    if (this.isLoadingMore || !this.hasMore) return;
    this.isLoadingMore = true;
    this.currentPage++;
    const moduleId = this.getSelectedModuleId();
    this.activityService.fetchApiActivities(this.searchQuery, this.currentPage, moduleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ items, hasMore }) => {
        this.activities = [...this.activities, ...items];
        this.totalCount = this.activities.length;
        this.hasMore = hasMore;
        this.isLoadingMore = false;
        this.updateGrouped();
        this.cdr.markForCheck();
      });
  }

  private loadApiActivities(search: string = ''): void {
    this.currentPage = 1;
    this.isLoading = true;
    const moduleId = this.getSelectedModuleId();
    this.activityService.fetchApiActivities(search, this.currentPage, moduleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ items, hasMore }) => {
          this.activities = items;
          this.totalCount = items.length;
          this.hasMore = hasMore;
          this.pinnedCount = 0;
          this.updateGrouped();
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        // Falls through to the existing empty state ("No activity yet") rather
        // than leaving the spinner stuck forever on a failed request.
        error: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBackdropClick(): void {
    this.onClose.emit();
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.searchSubject.next(query);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.loadApiActivities('');
  }

  onFilterChange(filterType: ActivityFilterType): void {
    this.activeFilter = filterType;
    this.loadApiActivities(this.searchQuery);
  }

  onTogglePin(id: string): void {
    const currentItem = this.activities.find((item) => item.id === id);
    if (!currentItem) return;
    const nextPinned = !currentItem.pinned;

    this.activityService
      .togglePin(id, nextPinned)
      .pipe(takeUntil(this.destroy$))
      .subscribe((updated) => {
        if (!updated) {
          this.toasterService.showErrorMessage(`Failed to ${nextPinned ? 'pin' : 'unpin'} activity`);
          return;
        }
        this.activities = this.activities.map((item) =>
          item.id === id ? { ...item, pinned: nextPinned } : item
        );
        this.pinnedCount = this.activities.filter((item) => item.pinned).length;
        this.updateGrouped();
        this.cdr.markForCheck();
        this.toasterService.showSuccessMessage(`Activity ${nextPinned ? 'pinned' : 'unpinned'} successfully`);
      });
  }

  /** True when the activity has a resolvable linked record and should be rendered as clickable. */
  isClickable(item: ActivityItem): boolean {
    return !!item.subjectType && item.subjectId !== undefined && item.subjectId !== null;
  }

  /**
   * Navigate to the related record's detail page and close the panel on success.
   * Silently ignored for activities with no linked record (e.g. local page-visit entries).
   */
  onActivityClick(item: ActivityItem): void {
    if (!this.isClickable(item)) {
      return;
    }
    const target = this.activityService.resolveActivityRoute(item);
    if (!target) {
      this.toasterService.showWarningMessage('This activity has no linked record.');
      return;
    }
    const extras = target.queryParams ? { queryParams: target.queryParams } : {};

    // Router.navigate() resolves `false` for two very different situations:
    // a genuinely blocked/failed navigation, AND Angular's default no-op when
    // the target URL is already the current one (no route change to make).
    // Repeat views of the same record are the common case here (the record
    // you're already on keeps showing up in its own Activity list) — treating
    // that as a failure showed "Unable to open the linked record" for a link
    // that was, in fact, already open. Only the genuine-failure path still
    // surfaces the error toast.
    //
    // Comparing the resolved URL tree (not just target.path) matters for
    // task/ticket routes, which all share one base path ('/tasks', '/tickets')
    // differentiated only by queryParams — a path-only check would wrongly
    // call every other task "already open" while viewing any task.
    const targetUrl = this.router.createUrlTree([target.path], extras).toString();
    if (this.router.url === targetUrl) {
      this.onClose.emit();
      return;
    }

    this.router.navigate([target.path], extras).then((ok) => {
      if (ok) {
        this.onClose.emit();
      } else {
        this.toasterService.showErrorMessage('Unable to open the linked record.');
      }
    });
  }

  /** Keyboard activation for accessible row interaction. */
  onActivityKeydown(event: KeyboardEvent, item: ActivityItem): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onActivityClick(item);
    }
  }

  onDelete(id: string): void {
    this.activityService
      .toggleDelete(id, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((deleted) => {
        if (!deleted) {
          this.toasterService.showErrorMessage('Failed to delete activity');
          return;
        }
        this.activities = this.activities.filter((item) => item.id !== id);
        this.totalCount = this.activities.length;
        this.updateGrouped();
        this.cdr.markForCheck();
        this.toasterService.showSuccessMessage('Activity deleted successfully');
      });
  }

  onClearAll(): void {
    const dialogRef = this.dialog.open(ConfirmationPopupComponent, {
      width: '500px',
      height: '300px',
      data: {
        title: 'Clear Activity History',
        message: 'Are you sure you want to clear Activity History?',
        description: '',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.activityService
        .clearHistory()
        .pipe(takeUntil(this.destroy$))
        .subscribe((cleared) => {
          if (!cleared) {
            this.toasterService.showErrorMessage('Failed to clear activity history');
            return;
          }
          this.loadApiActivities(this.searchQuery);
          this.toasterService.showSuccessMessage('Activity history cleared successfully');
        });
    });
  }

  /**
   * Relative time display for activity timestamps.
   *
   * Falls back to the server-rendered `timestampLabel` when the raw timestamp is
   * missing or unparseable, so an item shows the tenant-formatted date rather
   * than "Invalid Date".
   */
  formatRelativeTime(item: ActivityItem): string {
    const timestamp = item.timestamp;
    if (!Number.isFinite(timestamp)) return item.timestampLabel ?? '';

    const diffMs = Date.now() - timestamp;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;

    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    // Older than a week: prefer the tenant's own date format over a hardcoded one.
    return item.timestampLabel ?? new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  trackByGroup(_index: number, group: ActivityGroup): string {
    return group.label;
  }

  trackByActivity(_index: number, item: ActivityItem): string {
    return item.id;
  }

  private updateGrouped(): void {
    this.groupedActivities = this.activityService.getFilteredGrouped(
      this.activities,
      this.activeFilter,
      ''
    );
  }

  private getSelectedModuleId(): number | undefined {
    // "All" sends null/undefined module_id so backend returns all modules.
    return FILTER_MODULE_IDS[this.activeFilter];
  }
}
